import { ArrowDown, ArrowUp, RefreshCw, Search } from "lucide-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { userDashboardConfig } from "../../components/portfolio/configs/userDashboardConfig";
import type { PortfolioRow } from "../../components/portfolio/columns/columnTypes";
import {
  ADMIN_CUSTOMER_ACCOUNT_MAX_AGE_MS,
  ADMIN_CUSTOMER_ACCOUNT_QUERY_OPTIONS,
} from "../../hooks/useAdminCustomerViewAccountData";
import { useAdminCustomerViewPortfolioData } from "../../hooks/useAdminCustomerViewPortfolioData";
import { useStrategyData } from "../../hooks/useStrategyData";
import { holdingsToPortfolioRows } from "../../utils/holdingsToPortfolioRows";
import { getStrategyDisplayName } from "../../utils/strategyUtils";
import {
  formatAccountUsdOrNA,
  formatNumber,
  normalizeAccountNumber,
} from "../../components/portfolio/columns/formatters";
import { useAlphaStrategyHoldings } from "./useAlphaStrategyHoldings";
import type { AdminTargetPositionsAdapterProps } from "./alphaPageSubject";

type PositionsTab = "combined" | "actual" | "model";
type SortKey = "symbol" | "currentWeightPct" | "targetWeightPct";
type SupportingResource = "marketPrices" | "contractDetails" | "tradableTimes" | "fxRates";

export default function AdminTargetPositionsAdapter(
  props: AdminTargetPositionsAdapterProps
): React.ReactElement {
  const { subject, accountState, onRetryAccountPreflight } = props;
  const accountStateIsCurrent = accountState.ownerUserId === subject.userId
    && accountState.generation === subject.generation;
  const linked = accountStateIsCurrent && accountState.status === "linked";
  const [activeTab, setActiveTab] = useState<PositionsTab>(
    linked ? "combined" : "model"
  );
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("targetWeightPct");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryingSupport, setRetryingSupport] = useState<SupportingResource | null>(null);
  const refreshFlightRef = useRef<Promise<void> | null>(null);
  const supportFlightRef = useRef<{
    resource: SupportingResource;
    promise: Promise<void>;
  } | null>(null);

  const data = useAdminCustomerViewPortfolioData({
    targetUserId: subject.userId,
    config: userDashboardConfig,
    enabled: linked,
    accountMaxAgeMs: ADMIN_CUSTOMER_ACCOUNT_MAX_AGE_MS,
    accountQueryOptions: ADMIN_CUSTOMER_ACCOUNT_QUERY_OPTIONS,
  });
  const {
    strategies,
    reservedEntries,
    productLookup,
    productItems,
    basketLookup,
    isLoading: isLoadingStrategy,
    strategyError,
    refetchStrategies,
    refetchProducts,
  } = useStrategyData(subject.userId, { fetchHistory: false, fetchFactors: false });

  const holdingsInput = useMemo(
    () => [...strategies, ...reservedEntries].map((s) => ({
      entityType: s.entityType,
      productId: s.productId,
      weight: parseFloat(s.weight),
      driftedWeight: s.driftedWeight,
      displayName: s.entityType === "MANAGED_STRATEGY"
        ? `Plutus Managed #${s.productId}`
        : getStrategyDisplayName(s, productLookup),
    })),
    [strategies, reservedEntries, productLookup]
  );
  const holdings = useAlphaStrategyHoldings(holdingsInput, productItems, basketLookup, {
    accessMode: "admin",
    ownerUserId: subject.userId,
  });
  const retryHoldings = holdings.retry;

  const positionsOwned = linked
    && data.positionsError === null
    && data.positionsLoadedForUserId === subject.userId;
  const summaryOwned = linked
    && data.summaryError === null
    && data.summaryLoadedForUserId === subject.userId;
  const accountNlv = summaryOwned
    ? normalizeAccountNumber(data.accountSummary?.netLiquidationSecurity)
    : null;
  const actualRows = useMemo(
    () => positionsOwned ? data.rows : [],
    [data.rows, positionsOwned]
  );
  const modelRows = useMemo(
    () => holdingsToPortfolioRows(holdings.holdings, accountNlv, { modelOnly: true }),
    [holdings.holdings, accountNlv]
  );

  const actualRowsWithModel = useMemo(() => {
    if (!modelRows.length) return actualRows;
    const modelByConId = new Map(modelRows.map((row) => [row.conId, row.targetWeightPct]));
    return actualRows.map((row) => {
      const modelWeight = modelByConId.get(row.conId);
      return modelWeight !== undefined ? { ...row, targetWeightPct: modelWeight } : row;
    });
  }, [actualRows, modelRows]);

  const combinedRows = useMemo(() => {
    if (!positionsOwned) return modelRows;
    const merged: PortfolioRow[] = [];
    const seen = new Set<number>();
    const modelByConId = new Map(modelRows.map((row) => [row.conId, row]));
    for (const actual of actualRows) {
      seen.add(actual.conId);
      merged.push({
        ...actual,
        targetWeightPct: modelByConId.get(actual.conId)?.targetWeightPct ?? null,
      });
    }
    for (const model of modelRows) {
      if (seen.has(model.conId)) continue;
      merged.push({
        ...model,
        currentWeightPct: null,
        unrealizedPnlUsd: null,
        mktValueUsd: null,
        shares: 0,
        mktValue: 0,
        mktPrice: 0,
        avgCost: 0,
      });
    }
    return merged;
  }, [positionsOwned, actualRows, modelRows]);

  const actualDisabled = !linked || !positionsOwned;
  const combinedDisabled = !linked || (!positionsOwned && !summaryOwned);
  const effectiveTab = (activeTab === "actual" && actualDisabled) || (activeTab === "combined" && combinedDisabled)
    ? "model"
    : activeTab;

  const handleRefresh = useCallback((): Promise<void> => {
    if (refreshFlightRef.current) return refreshFlightRef.current;
    setIsRefreshing(true);
    const request = Promise.allSettled([
      Promise.resolve(refetchStrategies()),
      Promise.resolve(refetchProducts()),
      Promise.resolve(holdings.retry()),
      linked ? data.refreshAccount() : Promise.resolve(),
    ]).then(() => undefined);
    refreshFlightRef.current = request;
    const clear = () => {
      if (refreshFlightRef.current === request) {
        refreshFlightRef.current = null;
        setIsRefreshing(false);
      }
    };
    void request.then(clear, clear);
    return request;
  }, [refetchStrategies, refetchProducts, holdings, linked, data]);

  const handleAccountRefresh = useCallback((): Promise<void> => {
    if (refreshFlightRef.current) return refreshFlightRef.current;
    setIsRefreshing(true);
    const request = data.refreshAccount();
    refreshFlightRef.current = request;
    const clear = () => {
      if (refreshFlightRef.current === request) {
        refreshFlightRef.current = null;
        setIsRefreshing(false);
      }
    };
    void request.then(clear, clear);
    return request;
  }, [data]);

  const handleModelRetry = useCallback(() => {
    refetchStrategies();
    refetchProducts();
    retryHoldings();
  }, [refetchStrategies, refetchProducts, retryHoldings]);

  const handleSupportingRetry = useCallback((resource: SupportingResource): Promise<void> => {
    const existing = supportFlightRef.current;
    if (existing) return existing.promise;
    setRetryingSupport(resource);
    const request = data.retrySupportingData(resource);
    supportFlightRef.current = { resource, promise: request };
    const clear = () => {
      if (supportFlightRef.current?.promise === request) {
        supportFlightRef.current = null;
        setRetryingSupport(null);
      }
    };
    void request.then(clear, clear);
    return request;
  }, [data]);

  let accountBanner: React.ReactNode = null;
  if (!accountStateIsCurrent || accountState.status === "loading") {
    accountBanner = <div className="h-16 animate-pulse rounded-v7-panel bg-v7-mist" role="status" />;
  } else if (accountState.status === "none") {
    accountBanner = <h2 className="text-base font-semibold text-v7-ink">No linked brokerage account</h2>;
  } else if (accountState.status === "failed") {
    accountBanner = (
      <div>
        <h2 className="text-base font-semibold text-v7-ink">Account unavailable</h2>
        <p className="mt-1 text-sm text-v7-ink-soft">
          We could not verify this customer&apos;s linked brokerage account.
        </p>
        <button
          type="button"
          onClick={onRetryAccountPreflight}
          className="mt-3 rounded-v7-control bg-v7-ink px-3 py-2 text-sm font-medium text-v7-white"
        >
          Retry account check
        </button>
      </div>
    );
  }

  const supportingWarnings: Array<{
    resource: SupportingResource;
    message: string;
    retryLabel?: string;
  }> = [];
  if (positionsOwned && data.supportingData.marketPrices.isLoading) {
    supportingWarnings.push({ resource: "marketPrices", message: "Updating live prices." });
  } else if (positionsOwned && !data.supportingData.marketPrices.complete) {
    supportingWarnings.push({
      resource: "marketPrices",
      message: "Live prices are unavailable. Values may be delayed.",
      retryLabel: "Retry live prices",
    });
  }
  if (positionsOwned && data.supportingData.fxRates.isLoading) {
    supportingWarnings.push({ resource: "fxRates", message: "Updating FX rates." });
  } else if (positionsOwned && !data.supportingData.fxRates.complete) {
    supportingWarnings.push({
      resource: "fxRates",
      message: "FX rates are unavailable. USD values and portfolio weights are unavailable for affected positions.",
      retryLabel: "Retry FX rates",
    });
  }
  // Optional metadata falls back silently after a successful request.
  // Only a failed request presents an actionable retry.
  if (positionsOwned && data.supportingData.contractDetails.error !== null && !data.supportingData.contractDetails.isLoading) {
    supportingWarnings.push({
      resource: "contractDetails",
      message: "Some contract details are unavailable.",
      retryLabel: "Retry contract details",
    });
  }
  if (positionsOwned && data.supportingData.tradableTimes.error !== null && !data.supportingData.tradableTimes.isLoading) {
    supportingWarnings.push({
      resource: "tradableTimes",
      message: "Trading-hours details are unavailable.",
      retryLabel: "Retry trading-hours details",
    });
  }

  const modelLoading = holdings.isLoading || isLoadingStrategy;
  const modelFailed = strategyError !== null || holdings.error !== null;
  const modelComplete = !modelLoading && !modelFailed && holdings.complete;
  const combinedComplete = positionsOwned
    && summaryOwned
    && modelComplete;
  const rowsForTab = effectiveTab === "model"
    ? modelRows
    : effectiveTab === "combined"
      ? combinedRows
      : actualRowsWithModel;
  const activeSortKey: SortKey = effectiveTab === "actual" && sortKey === "targetWeightPct"
    ? "currentWeightPct"
    : effectiveTab === "model" && sortKey === "currentWeightPct"
      ? "targetWeightPct"
      : sortKey;
  const visible = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = normalizedQuery
      ? rowsForTab.filter((row) => row.symbol.toLowerCase().includes(normalizedQuery))
      : rowsForTab.slice();
    filtered.sort((left, right) => {
      const leftValue = activeSortKey === "symbol" ? left.symbol : left[activeSortKey];
      const rightValue = activeSortKey === "symbol" ? right.symbol : right[activeSortKey];
      if (leftValue === null && rightValue === null) return 0;
      if (leftValue === null) return 1;
      if (rightValue === null) return -1;
      const comparison = typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue));
      return sortDir === "asc" ? comparison : -comparison;
    });
    return filtered;
  }, [query, rowsForTab, activeSortKey, sortDir]);

  const toggleSort = (nextKey: SortKey) => {
    if (activeSortKey === nextKey) {
      setSortKey(nextKey);
      setSortDir((current) => current === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "symbol" ? "asc" : "desc");
  };

  return (
    <div data-testid="alpha-positions-content" className="mx-auto w-full max-w-[1080px] px-4 pb-16 pt-7 md:px-10">
      <header className="mb-6">
        <h1 className="font-v7-serif text-3xl font-normal text-v7-ink">Positions</h1>
      </header>
      {accountBanner}
      {linked && summaryOwned && (
        <dl className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-v7-ink/50">Net liquidation</dt>
            <dd className="ph-mask font-v7-serif text-lg">{formatAccountUsdOrNA(data.accountSummary?.netLiquidationSecurity)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-v7-ink/50">Cash</dt>
            <dd className="ph-mask font-v7-serif text-lg">{formatAccountUsdOrNA(data.accountSummary?.totalCashValueSecurity)}</dd>
          </div>
          {positionsOwned && (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-v7-ink/50">Positions</dt>
              <dd className="font-v7-serif text-lg">{formatNumber(actualRows.length, 0)}</dd>
            </div>
          )}
        </dl>
      )}
      {linked && data.summaryLoading && !summaryOwned && (
        <p role="status" className="mt-3 text-sm text-v7-ink-soft">Loading account summary.</p>
      )}
      {linked && data.positionsLoading && !positionsOwned && (
        <p role="status" className="mt-3 text-sm text-v7-ink-soft">Loading actual positions.</p>
      )}
      <div className="mt-6 inline-flex gap-1 rounded-v7-control border border-v7-ink/20 bg-v7-white p-0.5">
        {(["combined", "actual", "model"] as const).map((tab) => {
          const disabled = (tab === "actual" && actualDisabled) || (tab === "combined" && combinedDisabled);
          return (
            <button
              key={tab}
              type="button"
              disabled={disabled}
              onClick={() => { if (!disabled) setActiveTab(tab); }}
              className={`min-w-[84px] rounded-v7-control px-3 py-1.5 text-[13px] font-semibold ${
                effectiveTab === tab ? "bg-v7-bronze/10 text-v7-bronze-dark" : "text-v7-ink-soft"
              } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {tab === "combined" && linked && !combinedComplete
                ? "Combined (partial)"
                : tab[0].toUpperCase() + tab.slice(1)}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-v7-ink/40" aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search symbol"
            aria-label="Search positions by symbol"
            className="w-full rounded-v7-control border border-v7-ink/20 bg-v7-white py-2.5 pl-9 pr-3 text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => { void handleRefresh(); }}
          disabled={isRefreshing || data.positionsLoading || data.summaryLoading || isLoadingStrategy || holdings.isLoading}
          aria-label="Refresh positions"
          className="grid h-10 w-10 place-items-center rounded-v7-control border border-v7-ink/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
        </button>
      </div>
      {supportingWarnings.map((warning) => (
        <div
          key={warning.resource}
          role="status"
          className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-v7-bronze-dark"
        >
          <p>{warning.message}</p>
          {warning.retryLabel && (
            <button
              type="button"
              disabled={retryingSupport !== null}
              onClick={() => { void handleSupportingRetry(warning.resource); }}
              className="rounded-v7-control border border-v7-bronze/40 bg-v7-white px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {retryingSupport === warning.resource ? "Retrying..." : warning.retryLabel}
            </button>
          )}
        </div>
      ))}
      {effectiveTab !== "actual" && modelFailed && !modelLoading ? (
        <div role="alert" className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-v7-rust">
          <p>Model positions are unavailable.</p>
          <button
            type="button"
            onClick={handleModelRetry}
            className="rounded-v7-control border border-v7-rust/30 bg-v7-white px-3 py-1.5 text-xs font-medium"
          >
            Retry model
          </button>
        </div>
      ) : effectiveTab !== "actual" && !modelComplete && !modelLoading ? (
        <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-v7-bronze-dark">Partial model</p>
      ) : null}
      <div className="mt-4 overflow-hidden rounded-v7-panel border border-v7-ink/15 bg-v7-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-v7-ink/15">
              <th className="px-4 py-3 text-left" aria-sort={activeSortKey === "symbol" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
                <button type="button" onClick={() => toggleSort("symbol")} className="inline-flex items-center gap-1">
                  Symbol
                  {activeSortKey === "symbol" && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                </button>
              </th>
              {effectiveTab !== "model" && (
                <th className="px-4 py-3 text-right" aria-sort={activeSortKey === "currentWeightPct" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" onClick={() => toggleSort("currentWeightPct")} className="inline-flex items-center gap-1">
                    Current %
                    {activeSortKey === "currentWeightPct" && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                </th>
              )}
              {effectiveTab !== "actual" && (
                <th className="px-4 py-3 text-right" aria-sort={activeSortKey === "targetWeightPct" ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" onClick={() => toggleSort("targetWeightPct")} className="inline-flex items-center gap-1">
                    Model %
                    {activeSortKey === "targetWeightPct" && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={effectiveTab === "combined" ? 3 : 2} className="px-4 py-10 text-center text-sm text-v7-ink-soft">
                  {effectiveTab !== "actual" && modelLoading
                    ? "Loading model positions."
                    : effectiveTab !== "actual" && modelFailed
                      ? "Model positions are unavailable."
                      : query
                        ? "No positions match your search."
                        : effectiveTab !== "model" && data.positionsLoading
                          ? "Loading actual positions."
                          : effectiveTab !== "actual" && !modelComplete
                            ? "Model composition is incomplete."
                            : "No positions to show."}
                </td>
              </tr>
            ) : (
              visible.map((row: PortfolioRow) => (
                <tr key={`${row.conId}-${row.symbol}`} className="border-b border-v7-ink/10">
                  <td className="px-4 py-3">{row.symbol}</td>
                  {effectiveTab !== "model" && (
                    <td className="px-4 py-3 text-right font-mono">
                      {row.currentWeightPct == null ? "--" : formatNumber(row.currentWeightPct, 1)}
                    </td>
                  )}
                  {effectiveTab !== "actual" && (
                    <td className="px-4 py-3 text-right font-mono">
                      {row.targetWeightPct == null ? "--" : formatNumber(row.targetWeightPct, 1)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {linked && data.summaryError && (
        <p role="status" className="mt-3 text-sm text-v7-bronze-dark">Account summary is unavailable.</p>
      )}
      {linked && data.positionsError && (
        <p role="status" className="mt-3 text-sm text-v7-bronze-dark">Actual is unavailable.</p>
      )}
      {linked && (data.positionsError || data.summaryError) && (
        <button
          type="button"
          disabled={isRefreshing || data.positionsLoading || data.summaryLoading}
          onClick={() => { void handleAccountRefresh(); }}
          className="mt-3 rounded-v7-control border border-v7-ink/20 bg-v7-white px-3 py-2 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
        >
          Retry account data
        </button>
      )}
    </div>
  );
}
